import React, { useMemo, useState } from "react";
import { useCheckoutsFeedbacks } from "./hooks/feedbacks.hooks";
import ReservationFeedbacksTable from "./components/DataTable/ReservationFeedbacksTable";
import {
  CheckoutFeedback,
  CheckoutFeedbackFilter,
} from "@/services/interfaces/IFeedbackService";
import ReservationFeedbacksFilters from "./components/filters/reservation-feedbacks.filter";
import { ReservationFeedbackModal } from "./components/ReservationFeedbackModal";

const PER_PAGE = 10;
type SortBy = NonNullable<CheckoutFeedbackFilter["sortBy"]>;

export const ReservationFeedbacks: React.FC<{ hotelId: string }> = ({
  hotelId,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<
    Omit<CheckoutFeedbackFilter, "sortBy" | "page" | "limit" | "hotelId">
  >({
    roomIds: [],
    minRating: 1,
  });
  const [sortBy, setSortBy] = useState<SortBy | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const [selectedFeedback, setSelectedFeedback] =
    useState<CheckoutFeedback | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const feedbacksFilters = useMemo(() => {
    return {
      ...filters,
      page: currentPage,
      limit: PER_PAGE,
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
      hotelId,
    };
  }, [filters, currentPage, sortBy, sortOrder, hotelId]);

  const { data, isLoading } = useCheckoutsFeedbacks(feedbacksFilters);
  const feedbacks = data?.data ?? [];
  const totalPages = data?.total
    ? Math.max(1, Math.ceil(data?.total / data.limit))
    : 1;

  const handleSort = (field: string) => {
    const newField = field as SortBy;
    if (sortBy !== newField) {
      setSortBy(newField);
      setSortOrder("asc");
    } else {
      if (sortOrder === "asc") setSortOrder("desc");
      else if (sortOrder === "desc") {
        setSortBy(null);
        setSortOrder(null);
      } else setSortOrder("asc");
    }
  };

  const handleRowClick = (feedback: CheckoutFeedback) => {
    setSelectedFeedback(feedback);
    setIsModalOpen(true);
  };

  return (
    <div className="flex flex-col p-4 gap-4">
      <ReservationFeedbacksFilters filters={filters} onChange={setFilters} />

      <ReservationFeedbacksTable
        data={feedbacks}
        isLoading={isLoading}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        currentPage={currentPage}
        totalPages={totalPages || 1}
        onPageChange={setCurrentPage}
        onRowClick={handleRowClick}
      />

      <ReservationFeedbackModal
        feedback={selectedFeedback}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};
